import { c as _c } from "react/compiler-runtime";
import { createListCollection, DatePicker, Input, InputProps, Portal, Text, Textarea, TextareaProps, VStack } from '@chakra-ui/react';
import { parseDate } from '@internationalized/date';
import { ComboboxContent, ComboboxControl, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxItemText, ComboboxRoot } from '@src/components/chakra-ui/combobox';
import { NativeSelectField, NativeSelectRoot } from '@src/components/chakra-ui/native-select';
import { RadioCardItem, RadioCardRoot } from '@src/components/chakra-ui/radio-card';
import type { ChoiceItem, SelectItem } from '@src/components/sco/Advisors/utils/fastAgreementForm';
import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { LuCalendar } from 'react-icons/lu';
const LabelWithHelper = t0 => {
  const $ = _c(8);
  const {
    helper,
    label,
    labelFontWeight: t1
  } = t0;
  const labelFontWeight = t1 === undefined ? "semibold" : t1;
  let t2;
  if ($[0] !== label || $[1] !== labelFontWeight) {
    t2 = <Text as="span" fontWeight={labelFontWeight}>{label}</Text>;
    $[0] = label;
    $[1] = labelFontWeight;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== helper) {
    t3 = helper ? <><Text as="span" color="colorPalette.fg/40" fontWeight="normal">{" \xB7 "}</Text><Text as="span" color="colorPalette.fg/60" fontSize="xs" fontWeight="normal">{helper}</Text></> : null;
    $[3] = helper;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export const DocField = t0 => {
  const $ = _c(14);
  let children;
  let helper;
  let inputProps;
  let label;
  if ($[0] !== t0) {
    ({
      helper,
      label,
      children,
      ...inputProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = helper;
    $[3] = inputProps;
    $[4] = label;
  } else {
    children = $[1];
    helper = $[2];
    inputProps = $[3];
    label = $[4];
  }
  let t1;
  if ($[5] !== helper || $[6] !== label) {
    t1 = <Text as="p" color="colorPalette.fg" fontSize="sm" lineHeight="short"><LabelWithHelper helper={helper} label={label} /></Text>;
    $[5] = helper;
    $[6] = label;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== children || $[9] !== inputProps) {
    t2 = children ?? <Input borderColor="colorPalette.border" size="lg" variant="subtle" {...inputProps} />;
    $[8] = children;
    $[9] = inputProps;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t1 || $[12] !== t2) {
    t3 = <VStack align="stretch" gap={1.5} width="full">{t1}{t2}</VStack>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
export const DocTextarea = t0 => {
  const $ = _c(10);
  let helper;
  let label;
  let textareaProps;
  if ($[0] !== t0) {
    ({
      helper,
      label,
      ...textareaProps
    } = t0);
    $[0] = t0;
    $[1] = helper;
    $[2] = label;
    $[3] = textareaProps;
  } else {
    helper = $[1];
    label = $[2];
    textareaProps = $[3];
  }
  let t1;
  if ($[4] !== textareaProps) {
    t1 = <Textarea autoresize={true} borderColor="colorPalette.border" minH="32" size="lg" variant="subtle" {...textareaProps} />;
    $[4] = textareaProps;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== helper || $[7] !== label || $[8] !== t1) {
    t2 = <DocField helper={helper} label={label}>{t1}</DocField>;
    $[6] = helper;
    $[7] = label;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const DocSelect = t0 => {
  const $ = _c(15);
  const {
    disabled,
    helper,
    items,
    label,
    placeholder,
    value,
    onChange
  } = t0;
  let t1;
  if ($[0] !== onChange) {
    t1 = event => onChange(event.currentTarget.value);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== disabled || $[3] !== items || $[4] !== placeholder || $[5] !== t1 || $[6] !== value) {
    t2 = <NativeSelectField borderColor="colorPalette.border" disabled={disabled} items={items} placeholder={placeholder} value={value} onChange={t1} />;
    $[2] = disabled;
    $[3] = items;
    $[4] = placeholder;
    $[5] = t1;
    $[6] = value;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== disabled || $[9] !== t2) {
    t3 = <NativeSelectRoot disabled={disabled} size="lg" variant="subtle">{t2}</NativeSelectRoot>;
    $[8] = disabled;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== helper || $[12] !== label || $[13] !== t3) {
    t4 = <DocField helper={helper} label={label}>{t3}</DocField>;
    $[11] = helper;
    $[12] = label;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
const matchesSelectQuery = (item: SelectItem, query: string) => {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return item.label.toLowerCase().includes(needle) || item.value.toLowerCase().includes(needle);
};
export const DocCombobox = t0 => {
  const $ = _c(45);
  const {
    disabled,
    helper,
    items,
    label,
    placeholder,
    value,
    onChange
  } = t0;
  let t1;
  if ($[0] !== items || $[1] !== value) {
    t1 = items.find(item => item.value === value)?.label || "";
    $[0] = items;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selectedLabel = t1;
  const [query, setQuery] = useState(selectedLabel);
  let t2;
  if ($[3] !== query || $[4] !== selectedLabel) {
    t2 = !query.trim() || query === selectedLabel ? "" : query;
    $[3] = query;
    $[4] = selectedLabel;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const filterQuery = t2;
  let t3;
  if ($[6] !== filterQuery || $[7] !== items) {
    let t4;
    if ($[9] !== filterQuery) {
      t4 = item_0 => matchesSelectQuery(item_0, filterQuery);
      $[9] = filterQuery;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t3 = items.filter(t4);
    $[6] = filterQuery;
    $[7] = items;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const filteredItems = t3;
  let t4;
  if ($[11] !== filteredItems) {
    t4 = createListCollection({
      items: filteredItems,
      itemToString: _temp,
      itemToValue: _temp2
    });
    $[11] = filteredItems;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const collection = t4;
  let t5;
  let t6;
  if ($[13] !== selectedLabel) {
    t5 = () => {
      setQuery(selectedLabel);
    };
    t6 = [selectedLabel];
    $[13] = selectedLabel;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== value) {
    t7 = value ? [value] : [];
    $[16] = value;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = details => setQuery(details.inputValue);
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== selectedLabel) {
    t9 = details_0 => {
      if (!details_0.open) {
        setQuery(selectedLabel);
      }
    };
    $[19] = selectedLabel;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== items || $[22] !== onChange) {
    t10 = details_1 => {
      const next = details_1.value[0];
      if (next) {
        setQuery(items.find(item_3 => item_3.value === next)?.label || next);
        onChange(next);
      }
    };
    $[21] = items;
    $[22] = onChange;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== disabled || $[25] !== placeholder) {
    t11 = <ComboboxControl><ComboboxInput borderColor="colorPalette.border" disabled={disabled} placeholder={placeholder} onFocus={_temp3} /></ComboboxControl>;
    $[24] = disabled;
    $[25] = placeholder;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ComboboxEmpty>No matching jurisdictions</ComboboxEmpty>;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== filteredItems) {
    t13 = filteredItems.map(_temp4);
    $[28] = filteredItems;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13) {
    t14 = <ComboboxContent maxH="60" overflowY="auto">{t12}{t13}</ComboboxContent>;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== collection || $[33] !== disabled || $[34] !== query || $[35] !== t10 || $[36] !== t11 || $[37] !== t14 || $[38] !== t7 || $[39] !== t9) {
    t15 = <ComboboxRoot openOnClick={true} collection={collection} disabled={disabled} inputBehavior="autohighlight" inputValue={query} size="lg" value={t7} variant="subtle" width="full" onInputValueChange={t8} onOpenChange={t9} onValueChange={t10}>{t11}{t14}</ComboboxRoot>;
    $[32] = collection;
    $[33] = disabled;
    $[34] = query;
    $[35] = t10;
    $[36] = t11;
    $[37] = t14;
    $[38] = t7;
    $[39] = t9;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== helper || $[42] !== label || $[43] !== t15) {
    t16 = <DocField helper={helper} label={label}>{t15}</DocField>;
    $[41] = helper;
    $[42] = label;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  return t16;
};
export const DocChoiceGroup = t0 => {
  const $ = _c(16);
  const {
    disabled,
    helper,
    items,
    label,
    value,
    onChange
  } = t0;
  const t1 = value || null;
  let t2;
  if ($[0] !== onChange) {
    t2 = details => {
      if (details.value) {
        onChange(details.value);
      }
    };
    $[0] = onChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== disabled || $[3] !== items) {
    let t4;
    if ($[5] !== disabled) {
      t4 = item => <RadioCardItem key={item.value} disabled={disabled} indicatorPlacement="start" label={<LabelWithHelper helper={item.description} label={item.label} labelFontWeight="medium" />} value={item.value} width="full" />;
      $[5] = disabled;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t3 = items.map(t4);
    $[2] = disabled;
    $[3] = items;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[7] !== disabled || $[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <RadioCardRoot disabled={disabled} gap={1.5} size="xs" value={t1} variant="outline" width="full" onValueChange={t2}>{t3}</RadioCardRoot>;
    $[7] = disabled;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== helper || $[13] !== label || $[14] !== t4) {
    t5 = <DocField helper={helper} label={label}>{t4}</DocField>;
    $[12] = helper;
    $[13] = label;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
export const DocDateField = t0 => {
  const $ = _c(31);
  const {
    disabled,
    helper,
    label,
    placeholder,
    value,
    onChange
  } = t0;
  let t1;
  bb0: {
    if (!value) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    try {
      let t2;
      if ($[1] !== value) {
        t2 = parseDate(value);
        $[1] = value;
        $[2] = t2;
      } else {
        t2 = $[2];
      }
      let t3;
      if ($[3] !== t2) {
        t3 = [t2];
        $[3] = t2;
        $[4] = t3;
      } else {
        t3 = $[4];
      }
      t1 = t3;
    } catch {
      let t2;
      if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      t1 = t2;
    }
  }
  const parsed = t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      placement: "bottom-start"
    };
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = typeof Intl === "undefined" ? "UTC" : Intl.DateTimeFormat().resolvedOptions().timeZone;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== onChange) {
    t4 = details => {
      const next = details.value[0];
      if (!next) {
        return;
      }
      onChange(`${next.year}-${String(next.month).padStart(2, "0")}-${String(next.day).padStart(2, "0")}`);
    };
    $[8] = onChange;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = placeholder || "Pick a date";
  let t6;
  if ($[10] !== disabled || $[11] !== t5) {
    t6 = <Input asChild={true} borderColor="colorPalette.border" size="lg" variant="subtle"><DatePicker.Input disabled={disabled} pe="10" placeholder={t5} /></Input>;
    $[10] = disabled;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <LuCalendar />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== disabled) {
    t8 = <DatePicker.IndicatorGroup><DatePicker.Trigger aria-label="Open calendar" disabled={disabled}>{t7}</DatePicker.Trigger></DatePicker.IndicatorGroup>;
    $[14] = disabled;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t6 || $[17] !== t8) {
    t9 = <DatePicker.Control width="full">{t6}{t8}</DatePicker.Control>;
    $[16] = t6;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DatePicker.View view="day"><DatePicker.Header /><DatePicker.DayTable /></DatePicker.View>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <DatePicker.View view="month"><DatePicker.Header /><DatePicker.MonthTable /></DatePicker.View>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Portal><DatePicker.Positioner><DatePicker.Content>{t10}{t11}<DatePicker.View view="year"><DatePicker.Header /><DatePicker.YearTable /></DatePicker.View></DatePicker.Content></DatePicker.Positioner></Portal>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== disabled || $[23] !== parsed || $[24] !== t4 || $[25] !== t9) {
    t13 = <DatePicker.Root closeOnSelect={true} openOnClick={true} disabled={disabled} positioning={t2} size="lg" timeZone={t3} value={parsed} variant="subtle" width="full" onValueChange={t4}>{t9}{t12}</DatePicker.Root>;
    $[22] = disabled;
    $[23] = parsed;
    $[24] = t4;
    $[25] = t9;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== helper || $[28] !== label || $[29] !== t13) {
    t14 = <DocField helper={helper} label={label}>{t13}</DocField>;
    $[27] = helper;
    $[28] = label;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
};
function _temp(item_1) {
  return item_1.label;
}
function _temp2(item_2) {
  return item_2.value;
}
function _temp3(event) {
  event.currentTarget.select();
}
function _temp4(item_4) {
  return <ComboboxItem key={item_4.value} item={item_4}><ComboboxItemText>{item_4.label}</ComboboxItemText></ComboboxItem>;
}